// components/DevolucionCompra/DevolucionCompraForm.tsx
import React, { useState, useEffect, useContext } from 'react';
import { toast } from '../../utils/toast';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  AssignmentReturn as ReturnIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import devolucionCompraApi from '../../services/api/devolucionCompraApi';
import { motivosDevolucionService, IMotivoDevolucion } from '../../services/api/motivos-devolucion.service';
import { DarkModeContext } from '../../context/darkModeContext';
import Swal from 'sweetalert2';

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: '#FFFFFF', bgS: '#F7F7F5', bgT: '#F1F1EF', text: '#37352F',
    textS: '#6B6B6B', textT: '#9B9A97', border: '#E9E9E7', borderH: '#D3D3D0',
    accent: '#2383E2', accentBg: '#E8F0FE', success: '#0F7B6C', successBg: '#DBEDDB',
    warning: '#D9730D', warningBg: '#FADEC9', error: '#E03E3E', errorBg: '#FBE4E4',
    shadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  dark: {
    bg: '#191919', bgS: '#202020', bgT: '#2B2B2B', text: '#E8E8E6',
    textS: '#9B9A97', textT: '#6B6B6B', border: '#333333', borderH: '#444444',
    accent: '#529CCA', accentBg: '#143A52', success: '#4DAB9A', successBg: '#1A2F2A',
    warning: '#C77D48', warningBg: '#2E2215', error: '#D44C47', errorBg: '#3D2020',
    shadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
};

interface ArticuloCompra {
  id: number;
  IdArticulo: number;
  IdSucursal: number;
  CantidadComprada: number;
  StockActual: number;
  PrecioCompra: string;
  articulo: { Id: number; Nombre: string };
  sucursal: { Id: number; Nombre: string };
  // form state
  cantidadDevolver: number;
  cantidadDisponible: number;
  motivoId: number | '';
  observacion: string;
}

export const DevolucionCompraFormContent: React.FC = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [searchValue, setSearchValue] = useState('');
  const [ordenCompra, setOrdenCompra] = useState<any>(null);
  const [articulos, setArticulos] = useState<ArticuloCompra[]>([]);
  const [motivos, setMotivos] = useState<IMotivoDevolucion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMotivos, setLoadingMotivos] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    motivosDevolucionService.getAll()
      .then(data => setMotivos(data.filter(m => m.Activo && !m.Eliminado)))
      .catch(() => setError('No se pudieron cargar los motivos de devolución'))
      .finally(() => setLoadingMotivos(false));
  }, []);

  const calcularDisponible = (
    idArticulo: number,
    cantidadComprada: number,
    devoluciones: any[]
  ): number => {
    const devuelto = devoluciones
      .flatMap((d: any) => d.detalles || [])
      .filter((det: any) => det.IdArticulo === idArticulo)
      .reduce((sum: number, det: any) => sum + (det.Cantidad || 0), 0);
    return Math.max(0, cantidadComprada - devuelto);
  };

  const buscarOrden = async () => {
    const oc = searchValue.trim();
    if (!oc) { setError('Ingrese el número de orden de compra'); return; }
    setLoading(true);
    setError(null);
    setOrdenCompra(null);
    setArticulos([]);
    try {
      const [respOC, devolucionesExistentes] = await Promise.all([
        devolucionCompraApi.getCompraFinalizada(oc),
        devolucionCompraApi.findAll(),
      ]);

      if (!respOC?.data?.length) {
        setError('Orden de compra no encontrada o no está finalizada');
        return;
      }

      const orden = respOC.data[0];
      setOrdenCompra(orden.compra);

      const devsPorOC = (devolucionesExistentes || []).filter(
        (d: any) => d.OrdenCompra === orden.compra.OrdenCompra
      );

      const articulosIniciados: ArticuloCompra[] = (orden.articulosCompra || []).map((a: any) => ({
        ...a,
        cantidadDevolver: 0,
        cantidadDisponible: calcularDisponible(a.IdArticulo, a.CantidadComprada, devsPorOC),
        motivoId: '',
        observacion: '',
      }));

      setArticulos(articulosIniciados);
    } catch (e: any) {
      setError(e?.message || 'Error al buscar la orden de compra');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') buscarOrden();
  };

  const updateArticulo = (id: number, field: keyof ArticuloCompra, value: any) => {
    setArticulos(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const articulosValidos = articulos.filter(a => a.cantidadDevolver > 0 && a.motivoId);
  const puedeEnviar = articulosValidos.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!articulosValidos.length) {
      toast.warning('Seleccione al menos un artículo con cantidad y motivo');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        ordenCompra: ordenCompra.OrdenCompra,
        detalles: articulosValidos.map(a => ({
          IdArticulo: a.IdArticulo,
          Cantidad: a.cantidadDevolver,
          IdMotivoDevolucionCompra: a.motivoId as number,
          Observacion: a.observacion || '',
        })),
      };

      const creada = await devolucionCompraApi.create(payload);
      await devolucionCompraApi.procesarDevolucion(creada.Id);

      await Swal.fire({
        icon: 'success',
        title: '¡Devolución procesada!',
        text: `Se devolvieron ${articulosValidos.length} tipo(s) de artículo de OC-${ordenCompra.OrdenCompra}`,
        timer: 2500,
        showConfirmButton: false,
      });
      navigate('/devoluciones-compra');
    } catch (e: any) {
      toast.error('Error');
    } finally {
      setSubmitting(false);
    }
  };

  const cellSx = { borderBottom: `1px solid ${p.border}`, color: p.text, fontSize: '0.875rem', py: 1.5 };
  const headCellSx = {
    ...cellSx,
    color: p.textT, fontWeight: 600, fontSize: '0.72rem',
    letterSpacing: '0.04em', textTransform: 'uppercase' as const, bgcolor: p.bgS,
  };

  return (
    <Box sx={{ bgcolor: p.bgS, minHeight: '100vh', px: { xs: 2, md: 3 }, py: 3 }}>

        {/* ── Back ── */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/devoluciones-compra')}
          size="small"
          sx={{ color: p.textS, textTransform: 'none', mb: 2.5, fontWeight: 500, borderRadius: '7px' }}
        >
          Volver a devoluciones
        </Button>

        {/* ── Buscar OC ── */}
        <Box sx={{
          bgcolor: p.bg, border: `1px solid ${p.border}`, borderRadius: '10px',
          p: 3, mb: 3, boxShadow: p.shadow,
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text, mb: 0.5 }}>
            Buscar orden de compra
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: p.textT, mb: 2.5 }}>
            Ingrese el número exacto de la orden de compra finalizada para iniciar la devolución.
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ maxWidth: 480 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ej: OC-2024-001"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: p.textT }} /></InputAdornment>,
                sx: { bgcolor: p.bgS, borderRadius: '8px', color: p.text, '& fieldset': { borderColor: p.border } },
              }}
            />
            <Button
              variant="contained"
              onClick={buscarOrden}
              disabled={loading || !searchValue.trim()}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
              sx={{
                bgcolor: p.accent, color: '#fff', textTransform: 'none', fontWeight: 600,
                borderRadius: '8px', boxShadow: 'none', whiteSpace: 'nowrap',
                '&:hover': { bgcolor: darkMode ? '#3A7BA8' : '#1A6DC0', boxShadow: 'none' },
              }}
            >
              Buscar
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          {loadingMotivos && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }}>
              Cargando motivos de devolución...
            </Alert>
          )}
        </Box>

        {/* ── Datos de la OC ── */}
        {ordenCompra && (
          <Box sx={{
            bgcolor: p.bg, border: `1px solid ${p.border}`, borderRadius: '10px',
            p: 3, mb: 3, boxShadow: p.shadow,
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
              <ReturnIcon sx={{ color: p.accent, fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text }}>
                OC-{ordenCompra.OrdenCompra}
              </Typography>
              <Chip
                label="Finalizada"
                size="small"
                sx={{ bgcolor: p.successBg, color: p.success, fontWeight: 600, fontSize: '0.75rem', height: 22 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 1 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Proveedor
                </Typography>
                <Typography sx={{ color: p.text, fontWeight: 500 }}>
                  {ordenCompra.proveedor?.Nombre || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Registrada por
                </Typography>
                <Typography sx={{ color: p.text, fontWeight: 500 }}>
                  {ordenCompra.usuario?.Usuario || '—'}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* ── Tabla de artículos ── */}
        {articulos.length > 0 && (
          <Box sx={{
            bgcolor: p.bg, border: `1px solid ${p.border}`, borderRadius: '10px',
            boxShadow: p.shadow, overflow: 'hidden',
          }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${p.border}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text }}>
                Artículos a devolver
              </Typography>
              <Typography sx={{ fontSize: '0.83rem', color: p.textT, mt: 0.5 }}>
                Ingrese la cantidad a devolver, el motivo y opcionalmente una observación para cada artículo.
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Artículo</TableCell>
                    <TableCell sx={headCellSx} align="center">Comprada</TableCell>
                    <TableCell sx={headCellSx} align="center">Disponible</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 120 }} align="center">Devolver</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 200 }}>Motivo *</TableCell>
                    <TableCell sx={headCellSx}>Observación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articulos.map(art => {
                    const sinDisponible = art.cantidadDisponible === 0;
                    return (
                      <TableRow
                        key={art.id}
                        sx={{
                          opacity: sinDisponible ? 0.5 : 1,
                          '&:hover': { bgcolor: p.bgS },
                          '&:last-child td': { borderBottom: 'none' },
                        }}
                      >
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontWeight: 500, color: p.text }}>
                            {art.articulo?.Nombre || 'N/A'}
                          </Typography>
                          {sinDisponible && (
                            <Typography sx={{ fontSize: '0.75rem', color: p.error, mt: 0.25 }}>
                              Ya devuelto completamente
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Typography sx={{ color: p.textS }}>{art.CantidadComprada}</Typography>
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: '50%',
                            bgcolor: sinDisponible ? p.errorBg : p.bgT,
                            color: sinDisponible ? p.error : p.text,
                            fontSize: '0.82rem', fontWeight: 600,
                          }}>
                            {art.cantidadDisponible}
                          </Box>
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={art.cantidadDevolver || ''}
                            onChange={e => {
                              const val = Math.min(
                                Math.max(0, parseInt(e.target.value) || 0),
                                art.cantidadDisponible
                              );
                              updateArticulo(art.id, 'cantidadDevolver', val);
                            }}
                            disabled={sinDisponible}
                            inputProps={{ min: 0, max: art.cantidadDisponible, style: { textAlign: 'center' } }}
                            sx={{
                              width: 90,
                              '& input': { py: 0.75, px: 1, color: p.text },
                              '& fieldset': { borderColor: art.cantidadDevolver > 0 ? p.accent : p.border },
                              bgcolor: p.bgS, borderRadius: '7px',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={art.motivoId}
                              onChange={e => updateArticulo(art.id, 'motivoId', e.target.value)}
                              disabled={sinDisponible || art.cantidadDevolver === 0}
                              displayEmpty
                              sx={{
                                bgcolor: p.bgS, color: p.text, borderRadius: '7px',
                                '& fieldset': { borderColor: p.border },
                                '& .MuiSelect-select': { py: 0.75 },
                              }}
                            >
                              <MenuItem value="" disabled>
                                <Typography sx={{ color: p.textT, fontSize: '0.85rem' }}>Seleccionar...</Typography>
                              </MenuItem>
                              {motivos.map(m => (
                                <MenuItem key={m.Id} value={m.Id}>{m.Nombre}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Opcional"
                            value={art.observacion}
                            onChange={e => updateArticulo(art.id, 'observacion', e.target.value)}
                            disabled={sinDisponible || art.cantidadDevolver === 0}
                            multiline
                            maxRows={2}
                            sx={{
                              '& input, & textarea': { color: p.text, fontSize: '0.85rem' },
                              '& fieldset': { borderColor: p.border },
                              bgcolor: p.bgS, borderRadius: '7px',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Summary + Submit ── */}
            <Box sx={{ px: 3, py: 2.5, borderTop: `1px solid ${p.border}`, bgcolor: p.bgS }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                <Box>
                  {articulosValidos.length > 0 ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CheckIcon sx={{ color: p.success, fontSize: 18 }} />
                      <Typography sx={{ fontSize: '0.85rem', color: p.success, fontWeight: 600 }}>
                        {articulosValidos.length} artículo(s) listos para devolver (
                        {articulosValidos.reduce((s, a) => s + a.cantidadDevolver, 0)} unidades)
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: '0.85rem', color: p.textT }}>
                      Complete la cantidad y el motivo para al menos un artículo
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ReturnIcon />}
                  onClick={handleSubmit}
                  disabled={!puedeEnviar}
                  sx={{
                    bgcolor: p.accent, color: '#fff', textTransform: 'none',
                    fontWeight: 600, borderRadius: '8px', boxShadow: 'none',
                    px: 3, py: 1,
                    '&:hover': { bgcolor: darkMode ? '#3A7BA8' : '#1A6DC0', boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: p.bgT, color: p.textT },
                  }}
                >
                  {submitting ? 'Procesando...' : 'Crear y procesar devolución'}
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
  );
};

export default DevolucionCompraFormContent;
